/* PetalHush P-102b · photo pre-upload (theme/P-102-BAG-SPEED.md, "Phase 2").
   Goal: Add to bag ≈ 1 s whatever the photo. As soon as a chosen photo decodes, it is uploaded in the background to the
   Souly studio Worker through the App Proxy (C.upload.route, default /apps/ph-preview/up → { id, url }); Add to bag then
   posts only that URL in the photo property (same name as today) plus "_Photo id", instead of the file.
   Used by assets/petalhush-breed.js and assets/petalhush-product.js (their P-102b blocks call start() and apply()); loaded
   by both sections when theme setting ph_photo_up_on is on. Without this file, C.upload or a Turnstile key, nothing
   changes: the phase 1 multipart add (Shopify upload) runs exactly as before.
   - Which file goes up: HEIC/HEIF → converted here to JPEG at full size (≤ 16.7 MP, the iOS canvas limit), q 0.88 (as phase 1), white
     fill, orientation as the browser shows it — so the Worker and Ops v2 never see HEIC. A browser that can't decode HEIC
     never calls start() (the PDP only calls it once the photo decodes) → Shopify upload. JPEG/PNG/WebP → the phase 1
     copy when phase 1 made one (photo.slim, waited ≤ 5 s), else the original. > 20 MB, a blank-looking canvas → no
     pre-upload.
   - One fresh Turnstile token per upload (own invisible widget; shares the Turnstile script with the first look).
   - Replacing a photo in the same field aborts the old upload; drop(key) (called by the PDP when a photo is removed, or
     before a replacement decodes) aborts it too (QA m4).
   - apply(fd, list, onPct) at Add to bag: for each [field name, photo state] whose file is still in the FormData:
       done → fd.set(name, url), "_Photo id" = the ids; running → waits, onPct(n) shows its progress on the buttons and the
       bag ("Adding to your bag… n%"); failed, refused, or no progress for 60 s → aborted, the file stays in the FormData
       (phase 1 multipart add). Never loses the photo. Resolves the fields still sent as files.
   - The answer's URL must start with C.upload.prefix (theme setting ph_photo_up_host) and hold only URL-safe characters.
   - P-111 S1 (theme/P-111-FIRST-LOOK-SPEED.md): when the first look runs for the same photo, the PDP sets photo.pvGate
     (a promise, resolved once its /start has answered, failed or been skipped). The upload itself waits for it, at most
     15 s from the decode, so the small first-look copy has the phone's uplink to itself. The file prep and the Turnstile
     token still run during the wait. apply() (Add to bag) ends the wait at once. No pvGate (other photo fields, first
     look off, no site key) = starts at once, as before. */
(function () {
  if (window.PetalHushUp) return;
  var STALL = 60000, MAXB = 20 * 1024 * 1024, PIX = 16777216, SLIM_WAIT = 5000;
  var GATE_MAX = 15000;   /* P-111 S1: the longest the upload waits for the first look's /start */
  var cur = {}, tsP = null, bidMem = '';

  function heic(f) { return /^image\/hei[cf]/i.test(f.type || '') || /\.(heic|heif)$/i.test(f.name || ''); }
  function base(f) { return String(f.name || '').replace(/\.[^.]*$/, '') || 'photo'; }
  function later(p, ms, v) { return Promise.race([p, new Promise(function (r) { setTimeout(function () { r(v); }, ms); })]); }
  function bid() {   // same per-browser id as the first look (limits per browser per day, server side)
    var make = function () { return window.crypto && crypto.randomUUID ? crypto.randomUUID() : 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); };
    try { var b = localStorage.getItem('ph_bid'); if (!b) { b = make(); localStorage.setItem('ph_bid', b); } return b; }
    catch (e) { return bidMem || (bidMem = make()); }
  }
  function jpeg(img, q) {   // full size (≤ 16.7 MP), white fill; null when the canvas can't or looks blank
    return new Promise(function (res) {
      try {
        var w = img.naturalWidth, h = img.naturalHeight; if (!w || !h) return res(null);
        var k = Math.min(1, Math.sqrt(PIX / (w * h))), c = document.createElement('canvas');
        if (!c.getContext || !c.toBlob) return res(null);
        c.width = Math.max(1, Math.floor(w * k)); c.height = Math.max(1, Math.floor(h * k));
        var g = c.getContext('2d'); if (!g) return res(null);
        g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); g.drawImage(img, 0, 0, c.width, c.height);
        var px = c.width * c.height;
        c.toBlob(function (b) { c.width = c.height = 0; res(b && b.size > px * 0.02 ? b : null); }, 'image/jpeg', q);
      } catch (e) { res(null); }
    });
  }
  function fileFor(photo, img) {
    var f = photo.file;
    if (heic(f)) return jpeg(img, 0.88).then(function (b) { return b && { blob: b, name: base(f) + '.jpg' }; });
    return later(photo.slimP || Promise.resolve(null), SLIM_WAIT, null).then(function (b) {
      var ok = b || /^image\/(jpeg|png|webp)$/i.test(f.type || '') || /\.(jpe?g|png|webp)$/i.test(f.name || '');
      return ok ? { blob: b || f, name: b ? base(f) + '.jpg' : f.name } : null;
    });
  }
  function loadTs() {   // Cloudflare Turnstile script (the first look may already have added it)
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!tsP) tsP = new Promise(function (res, rej) {
      var have = document.querySelector('script[src*="challenges.cloudflare.com/turnstile/"]'), t0 = Date.now();
      (function wait() {
        if (window.turnstile) return res(window.turnstile);
        if (Date.now() - t0 > 20000) { tsP = null; return rej(); }
        setTimeout(wait, 100);
      })();
      if (!have) {
        var sc = document.createElement('script');
        sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'; sc.async = true;
        sc.onerror = function () { tsP = null; rej(); };
        document.head.appendChild(sc);
      }
    });
    return tsP;
  }
  function token(key, host) {   // one fresh invisible token ('' = none)
    return loadTs().then(function (ts) {
      return new Promise(function (res) {
        var box = document.createElement('div'), id = null, over = false;
        box.className = 'ph-pv-ts'; box.style.cssText = 'position:fixed;right:12px;bottom:12px;z-index:2147483000';   // shown only if Turnstile must ask
        (host || document.body).appendChild(box);
        var done = function (v) {
          if (over) return; over = true; clearTimeout(to);
          setTimeout(function () { try { if (id !== null) ts.remove(id); } catch (e) { /* gone */ } if (box.parentNode) box.parentNode.removeChild(box); }, 0);
          res(v || '');
        };
        var to = setTimeout(function () { done(''); }, 20000);
        try { id = ts.render(box, { sitekey: key, appearance: 'interaction-only', callback: done, 'error-callback': function () { done(''); }, 'expired-callback': function () { done(''); } }); }
        catch (e) { done(''); }
      });
    }).catch(function () { return ''; });
  }
  /* P-111 S1: resolves when this photo's first look has its /start answered (photo.pvGate), after 15 s from the decode,
     or at once when apply() calls u.go() (Add to bag) or there is no first look for this photo. Never rejects. */
  function gate(photo, u) {
    return new Promise(function (res) {
      var to = null, over = false;
      var go = function () { if (over) return; over = true; clearTimeout(to); u.go = null; u.gated = false; res(); };
      u.go = go; u.gated = true;
      var g = photo.pvGate;   // set by the PDP's PV.start in the same decode handler (always before this runs)
      if (u.rush || !g || typeof g.then !== 'function') return go();   // rush = Add to bag came first
      to = setTimeout(go, Math.max(0, GATE_MAX - (Date.now() - u.t0)));
      g.then(go, go);
    });
  }
  /* P-111 end */
  function fail(u, why) {
    var x = u.xhr; u.state = 'fail'; u.why = why; u.xhr = null;
    if (x) try { x.abort(); } catch (e) { /* done */ }
  }
  function send(u, x, tok, cfg) {
    var fd = new FormData();
    fd.append('photo', x.blob, x.name); fd.append('bid', bid()); fd.append('turnstile', tok);
    var r = new XMLHttpRequest();
    u.xhr = r; u.state = 'up'; u.last = Date.now(); u.bytes = x.blob.size;
    r.open('POST', cfg.route, true);
    r.setRequestHeader('Accept', 'application/json');
    if (r.upload) r.upload.onprogress = function (e) {
      if (e.lengthComputable && e.total) { u.pct = Math.min(99, Math.floor(e.loaded / e.total * 100)); u.last = Date.now(); }
    };
    r.onload = function () {
      if (u.state !== 'up') return;
      var j = null; try { j = JSON.parse(r.responseText); } catch (e) { /* not JSON */ }
      var ok = r.status === 200 && j && typeof j.url === 'string' && typeof j.id === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(j.id)
        && !!cfg.prefix && j.url.indexOf(cfg.prefix) === 0 && /^https:\/\/[A-Za-z0-9._\/-]+$/.test(j.url);
      if (!ok) return fail(u, 'answer ' + r.status + (j && j.status ? ' ' + j.status : ''));
      u.state = 'done'; u.pct = 100; u.url = j.url; u.id = j.id; u.xhr = null;
    };
    r.onerror = r.ontimeout = function () { if (u.state === 'up') fail(u, 'network'); };
    r.send(fd);
  }
  function start(photo, img, C, key) {
    var cfg = C && C.upload;
    if (!cfg || !cfg.route || !cfg.sitekey || !cfg.prefix || !photo || !photo.file || !window.FormData || !window.XMLHttpRequest) return;
    var prev = cur[key];
    if (prev && prev !== photo && prev.up && (prev.up.state === 'prep' || prev.up.state === 'up')) fail(prev.up, 'replaced');
    cur[key] = photo;
    var u = photo.up = { state: 'prep', pct: 0, last: Date.now(), xhr: null, t0: Date.now() };
    fileFor(photo, img).then(function (x) {
      if (u.state !== 'prep') return;
      if (!x || !x.blob || !x.blob.size || x.blob.size > MAXB) return fail(u, 'file');
      /* P-111 S1: token and first-look wait side by side; the upload starts once both are over */
      return Promise.all([token(cfg.sitekey, cfg.host), gate(photo, u)]).then(function (r) {
        var tok = r[0];
        /* P-111 end */
        if (u.state !== 'prep') return;
        if (!tok) return fail(u, 'token');
        send(u, x, tok, cfg);
      });
    }).catch(function () { if (u.state === 'prep') fail(u, 'error'); });
  }
  function apply(fd, list, onPct) {
    onPct = onPct || function () {};
    var items = list.filter(function (x) {
      var p = x[1], u = p && p.up, v = fd.get(x[0]);
      return u && u.state !== 'fail' && v && typeof v !== 'string' && v.size === p.file.size && v.name === p.file.name;
    });
    if (!items.length) return Promise.resolve(list);
    items.forEach(function (x) { var u = x[1].up; u.rush = true; if (u.go) u.go(); });   /* P-111 S1: Add to bag = upload now */
    return new Promise(function (res) {
      (function tick() {
        var now = Date.now(), wait = false, sum = 0;
        items.forEach(function (x) {
          var u = x[1].up;
          if ((u.state === 'prep' || u.state === 'up') && now - u.last > STALL) fail(u, 'stalled');
          if (u.state === 'prep' || u.state === 'up') wait = true;
          sum += u.state === 'done' ? 100 : u.pct;
        });
        if (wait) { onPct(Math.min(99, Math.floor(sum / items.length))); return setTimeout(tick, 200); }
        onPct(null);
        var ids = [];
        var rest = list.filter(function (x) {
          var u = items.indexOf(x) !== -1 && x[1].up;
          if (!u || u.state !== 'done') return true;
          fd.set(x[0], u.url); ids.push(u.id);
          return false;
        });
        if (ids.length) fd.set('properties[_Photo id]', ids.join(','));
        res(rest);
      })();
    });
  }
  function drop(key) {
    var p = cur[key];
    if (p && p.up && (p.up.state === 'prep' || p.up.state === 'up')) fail(p.up, 'removed');
    delete cur[key];
  }
  window.PetalHushUp = { start: start, apply: apply, drop: drop, _cur: cur };
})();
